// ==================================================
// IMPORTS
// ==================================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// ==================================================
// STAFF BLOCK CONFIGURATION
//
// Purpose:
// Defines available block types.
//
// Examples:
// Lesson Planning
// Lunch
// Training
// DND
// Vacation
// ==================================================
const STAFF_BLOCK_TYPES = [
    { value: 'lesson_planning', label: 'Lesson planning', type: 'focus', icon: '📚' },
    { value: 'lunch', label: 'Lunch', type: 'away', icon: '🍽️' },
    { value: 'break', label: 'Break', type: 'away', icon: '☕' },
    { value: 'vacation', label: 'Vacation', type: 'away', icon: '🌴' },
    { value: 'parent_meeting', label: 'Parent meeting', type: 'focus', icon: '👪' },
    { value: 'training', label: 'Training', type: 'focus', icon: '🎓' },
    { value: 'focus_time', label: 'Focus time', type: 'focus', icon: '🌙' },
    { value: 'dnd', label: 'Do Not Disturb', type: 'dnd', icon: '⛔' },
    { value: 'away', label: 'Away', type: 'away', icon: '🟡' },
];

// ==================================================
// COMPONENT: DND SCHEDULE MODAL
//
// Purpose:
// Create, edit, and manage staff
// availability blocks and DND schedules.
// ==================================================
const DndScheduleModal = ({
    showDndScheduleModal,
    setShowDndScheduleModal,
    savedDndSchedules,
    setSavedDndSchedules,
    getAuthHeaders,
    setDndScheduleName,
    setDndStartTime,
    setDndEndTime,
    setDndStartDate,
    setDndEndDate,
    setDndRepeat,
    setDndRepeatDays,
    setDndVisibleTo,
    setDndNotes,
    setSendInAppNotification,
    setSendEmailNotification,
    setEditingDndScheduleId,
    editingDndScheduleId,
    showCreateDndSchedule,
    setShowCreateDndSchedule,
    dndScheduleName,
    dndStartTime,
    dndEndTime,
    dndStartDate,
    dndEndDate,
    dndRepeat,
    dndRepeatDays,
    dndVisibleTo,
    dndNotes,
    sendInAppNotification,
    sendEmailNotification,
    useCenterTimezone,
    dndBypassRules,
    formatTypedTime,
    setCurrentStatus,

}) => {

    // ==================================================
    // LOCAL STATE MANAGEMENT
    //
    // Purpose:
    // Modal state
    // Staff selection
    // Repeat scheduling
    // Notification preferences
    // ==================================================
    const [sendPushNotification, setSendPushNotification] = useState(false);
    const [staffBlockType, setStaffBlockType] = useState('dnd');
    const [monthlyRepeatMode, setMonthlyRepeatMode] = useState('day_of_month');
    const [monthlyRepeatDates, setMonthlyRepeatDates] = useState([]);
    const [yearlyRepeatMode, setYearlyRepeatMode] = useState('month_day');
    const [yearlyRepeatMonths, setYearlyRepeatMonths] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState('');

    // ==================================================
    // UTILITY FUNCTIONS
    //
    // Purpose:
    // Formatting helpers used
    // throughout the modal.
    // ==================================================
    const ordinal = (number) => {
        if ([11, 12, 13].includes(number % 100)) return `${number}th`;

        const last = number % 10;

        if (last === 1) return `${number}st`;
        if (last === 2) return `${number}nd`;
        if (last === 3) return `${number}rd`;

        return `${number}th`;
    };

    const formatScheduleDisplayTime = (time) => {
        if (!time) return '';

        if (/^\d{2}:\d{2}$/.test(time)) {
            const [hour, minute] = time.split(':').map(Number);

            return new Date(2000, 0, 1, hour, minute).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
            });
        }

        return time;
    };

    // ==================================================
    // DERIVED VALUES
    //
    // Purpose:
    // Calculate selected block type,
    // selected staff, date labels,
    // week numbers, and display text.
    // ==================================================
    const selectedBlockType = STAFF_BLOCK_TYPES.find(
        (block) => block.value === staffBlockType
    );


    const selectedStartDate = dndStartDate
        ? new Date(`${dndStartDate}T00:00:00`)
        : new Date();

    const dayOfMonth = selectedStartDate.getDate();

    const weekdayName = selectedStartDate.toLocaleDateString([], {
        weekday: 'long',
    });

    const monthName = selectedStartDate.toLocaleDateString([], {
        month: 'long',
    });

    const weekNumber = Math.ceil(dayOfMonth / 7);

    const weekText = ordinal(weekNumber);

    const selectedStaff = staffList.find(
        (staff) => staff._id === selectedStaffId || staff.id === selectedStaffId
    );

    const selectedStaffName =
        selectedStaff?.name ||
        selectedStaff?.fullName ||
        selectedStaff?.email ||
        'Staff';

    // ==================================================
    // DATA LOADING
    //
    // Purpose:
    // Load available staff members
    // when modal opens.
    // ================================================== 
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const res = await fetch('/api/users?role=staff', {
                    headers: getAuthHeaders(),
                });

                if (!res.ok) return;

                const data = await res.json();
                setStaffList(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Fetch staff failed:', err);
            }
        };

        if (showDndScheduleModal) {
            fetchStaff();
        }
    }, [showDndScheduleModal]);

    if (!showDndScheduleModal) return null;



    // ==================================================
    // MODAL RENDER UI
    //
    // Purpose:
    // Availability block scheduler
    // Repeat options
    // Notifications
    // Visibility controls
    // Staff assignment
    // ==================================================
    return (
        <div
            className="status-modal-overlay"
            onClick={() => setShowDndScheduleModal(false)}
        >
            <div
                className="dnd-schedule-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="status-modal-header">
                    <div>
                        <h2>Availability Blocks</h2>
                        <p className="dnd-schedule-subtitle">
                            Set quiet time or planning blocks.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="status-close-btn"
                        onClick={() => setShowDndScheduleModal(false)}
                    >
                        ✕
                    </button>
                </div>

                {/* ----- Saved Schedule List ----- */}
                <div className="dnd-schedule-list">

                    {savedDndSchedules
                        .filter((schedule) => {
                            if (!schedule.enabled) return false;

                            if (
                                schedule.repeat === 'none' &&
                                schedule.expiresAt &&
                                new Date(schedule.expiresAt).getTime() <= Date.now()
                            ) {
                                return false;
                            }

                            return true;
                        })
                        .map((schedule) => (
                            <div
                                key={schedule._id || schedule.id}
                                className="dnd-schedule-card"
                            >
                                <input
                                    type="checkbox"
                                    checked={schedule.enabled}
                                    onChange={async () => {
                                        try {
                                            const scheduleId = schedule._id || schedule.id;
                                            const nextEnabled = !schedule.enabled;

                                            const res = await fetch(`/api/focus-schedules/${scheduleId}`, {
                                                method: 'PATCH',
                                                headers: getAuthHeaders(),
                                                body: JSON.stringify({ enabled: nextEnabled }),
                                            });

                                            if (!res.ok) throw new Error('Failed to update schedule');

                                            const updatedSchedule = await res.json();

                                            setSavedDndSchedules((prev) =>
                                                prev.map((item) =>
                                                    (item._id || item.id) === scheduleId ? updatedSchedule : item
                                                )
                                            );

                                            window.dispatchEvent(
                                                new Event('kuddle:focus-schedules-updated')
                                            );

                                        } catch (err) {
                                            console.error(err);
                                            toast.error('Unable to update');
                                        }
                                    }}
                                />




                                <div className="dnd-schedule-card-content">
                                    <div className="dnd-schedule-header">
                                        <span className="dnd-schedule-name">
                                            {schedule.assignedStaffName
                                                ? `${schedule.assignedStaffName} · ${schedule.name}`
                                                : schedule.name}
                                        </span>
                                        <span className="dnd-schedule-status">
                                            {schedule.enabled ? 'Active' : 'Paused'}
                                        </span>
                                    </div>

                                    <div className="dnd-schedule-time">
                                        {(schedule.repeat === 'weekly' || schedule.repeat === 'custom')
                                            ? schedule.repeatDays?.join(', ')
                                            : schedule.repeat === 'daily'
                                                ? 'Daily'
                                                : schedule.repeat === 'monthly'
                                                    ? 'Monthly'
                                                    : 'One-time'}
                                        {' · '}
                                        {formatScheduleDisplayTime(schedule.startTime)} - {formatScheduleDisplayTime(schedule.endTime)}
                                    </div>

                                    {schedule.notes && (
                                        <div className="dnd-schedule-notes">
                                            <strong>Note:</strong> {schedule.notes}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className="edit-schedule-btn"
                                        onClick={() => {
                                            setDndScheduleName(schedule.name || '');
                                            setDndStartTime(schedule.startTime || '19:00');
                                            setDndEndTime(schedule.endTime || '07:00');
                                            setDndStartDate(
                                                schedule.startDate
                                                    ? schedule.startDate.slice(0, 10)
                                                    : ''
                                            );

                                            setDndEndDate(
                                                schedule.endDate
                                                    ? schedule.endDate.slice(0, 10)
                                                    : ''
                                            );
                                            setDndRepeat(schedule.repeat || 'none');
                                            setDndRepeatDays(schedule.repeatDays || []);
                                            setDndVisibleTo(schedule.visibleTo || ['admin', 'director', 'staff']);
                                            setDndNotes(schedule.notes || '');
                                            setSendInAppNotification(schedule.sendInAppNotification ?? true);
                                            setSendEmailNotification(schedule.sendEmailNotification ?? false);
                                            setEditingDndScheduleId(schedule._id || schedule.id);
                                            setShowCreateDndSchedule(true);
                                            setSelectedStaffId(schedule.assignedTo || '');
                                            setStaffBlockType(schedule.blockType || 'dnd');
                                        }}
                                    >
                                        Edit
                                    </button>

                                    <button
                                        type="button"
                                        className="delete-schedule-btn"
                                        onClick={async () => {
                                            try {
                                                const scheduleId = schedule._id || schedule.id;

                                                const res = await fetch(`/api/focus-schedules/${scheduleId}`, {
                                                    method: 'DELETE',
                                                    headers: getAuthHeaders(),
                                                });

                                                if (!res.ok) throw new Error('Failed to delete schedule');

                                                setSavedDndSchedules((prev) =>
                                                    prev.filter((item) => (item._id || item.id) !== scheduleId)
                                                );

                                                window.dispatchEvent(new Event('kuddle:focus-schedules-updated'));
                                                window.dispatchEvent(new Event('kuddle:calendar-updated'));

                                            } catch (err) {
                                                console.error(err);
                                                toast.error('Unable to remove');
                                            }
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}

                    <button
                        type="button"
                        className="dnd-create-schedule-btn"
                        onClick={() => setShowCreateDndSchedule((prev) => !prev)}
                    >
                        ＋ Create block
                    </button>

                    {/* ----- Create Schedule Panel ----- */}
                    {showCreateDndSchedule && (
                        <div className="dnd-create-panel">

                            <label className="dnd-field-label">Assign to staff</label>

                            <select
                                className="dnd-input"
                                value={selectedStaffId}
                                onChange={(e) => setSelectedStaffId(e.target.value)}
                            >
                                <option value="">Select staff</option>

                                {staffList.map((staff) => (
                                    <option key={staff._id || staff.id} value={staff._id || staff.id}>
                                        {staff.name || staff.fullName || staff.email}
                                    </option>
                                ))}
                            </select>

                            <label className="dnd-field-label">Block type</label>
                            <select
                                className="dnd-input"
                                value={staffBlockType}
                                onChange={(e) => setStaffBlockType(e.target.value)}
                            >
                                {STAFF_BLOCK_TYPES.map((block) => (
                                    <option key={block.value} value={block.value}>
                                        {block.icon} {block.label}
                                    </option>
                                ))}
                            </select>

                            <div className="dnd-flex-grid">
                                <label>
                                    Start date
                                    <input
                                        type="date"
                                        value={dndStartDate}
                                        onChange={(e) => setDndStartDate(e.target.value)}
                                    />
                                </label>

                                <label>
                                    <span className="dnd-label-inline">
                                        End date <span className="optional-label">(optional)</span>
                                    </span>
                                    <input
                                        type="date"
                                        value={dndEndDate}
                                        onChange={(e) => setDndEndDate(e.target.value)}
                                    />
                                </label>
                            </div>

                            <div className="dnd-time-grid">
                                <label className="dnd-time-field">
                                    Start time
                                    <input
                                        type="time"
                                        value={dndStartTime}
                                        onChange={(e) => setDndStartTime(e.target.value)}
                                    />
                                </label>

                                <label className="dnd-time-field">
                                    End time
                                    <input
                                        type="time"
                                        value={dndEndTime}
                                        onChange={(e) => setDndEndTime(e.target.value)}
                                    />
                                </label>
                            </div>

                            <label>
                                Repeat
                                <select
                                    value={dndRepeat}
                                    onChange={(e) => setDndRepeat(e.target.value)}
                                >
                                    <option value="none">Does not repeat</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </label>

                            {dndRepeat === 'weekly' && (
                                <div className="dnd-day-chip-grid">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            className={
                                                dndRepeatDays.includes(day)
                                                    ? 'dnd-day-chip selected'
                                                    : 'dnd-day-chip'
                                            }
                                            onClick={() =>
                                                setDndRepeatDays((prev) =>
                                                    prev.includes(day)
                                                        ? prev.filter((item) => item !== day)
                                                        : [...prev, day]
                                                )
                                            }
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {dndRepeat === 'monthly' && (
                                <div className="dnd-visible-section">
                                    <p className="dnd-menu-title">Monthly repeat options</p>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="monthlyRepeatMode"
                                            checked={monthlyRepeatMode === 'day_of_month'}
                                            onChange={() => setMonthlyRepeatMode('day_of_month')}
                                        />
                                        Repeat on day {dayOfMonth} every month
                                    </label>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="monthlyRepeatMode"
                                            checked={monthlyRepeatMode === 'weekday_of_month'}
                                            onChange={() => setMonthlyRepeatMode('weekday_of_month')}
                                        />
                                        Repeat on the {ordinal(weekNumber)} {weekdayName} of every month
                                    </label>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="monthlyRepeatMode"
                                            checked={monthlyRepeatMode === 'selected_dates'}
                                            onChange={() => setMonthlyRepeatMode('selected_dates')}
                                        />
                                        Select dates of the month to repeat
                                    </label>

                                    {monthlyRepeatMode === 'selected_dates' && (
                                        <div className="dnd-day-chip-grid">
                                            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    className={
                                                        monthlyRepeatDates.includes(day)
                                                            ? 'dnd-day-chip selected'
                                                            : 'dnd-day-chip'
                                                    }
                                                    onClick={() =>
                                                        setMonthlyRepeatDates((prev) =>
                                                            prev.includes(day)
                                                                ? prev.filter((item) => item !== day)
                                                                : [...prev, day]
                                                        )
                                                    }
                                                >
                                                    {day}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {dndRepeat === 'yearly' && (
                                <div className="dnd-visible-section">
                                    <p className="dnd-menu-title">Yearly repeat options</p>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="yearlyRepeatMode"
                                            checked={yearlyRepeatMode === 'month_day'}
                                            onChange={() => setYearlyRepeatMode('month_day')}
                                        />
                                        Repeat every year on {monthName} {dayOfMonth}
                                    </label>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="yearlyRepeatMode"
                                            checked={yearlyRepeatMode === 'weekday_of_month'}
                                            onChange={() => setYearlyRepeatMode('weekday_of_month')}
                                        />
                                        Repeat every year on the {ordinal(weekNumber)} {weekdayName} of {monthName}
                                    </label>

                                    <label className="dnd-check-row">
                                        <input
                                            type="radio"
                                            name="yearlyRepeatMode"
                                            checked={yearlyRepeatMode === 'selected_months'}
                                            onChange={() => setYearlyRepeatMode('selected_months')}
                                        />
                                        Repeat on day {dayOfMonth} of selected months
                                    </label>

                                    {yearlyRepeatMode === 'selected_months' && (
                                        <div className="dnd-day-chip-grid">
                                            {[
                                                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
                                            ].map((month) => (
                                                <button
                                                    key={month}
                                                    type="button"
                                                    className={
                                                        yearlyRepeatMonths.includes(month)
                                                            ? 'dnd-day-chip selected'
                                                            : 'dnd-day-chip'
                                                    }
                                                    onClick={() =>
                                                        setYearlyRepeatMonths((prev) =>
                                                            prev.includes(month)
                                                                ? prev.filter((item) => item !== month)
                                                                : [...prev, month]
                                                        )
                                                    }
                                                >
                                                    {month}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="dnd-visible-section">
                                <p className="dnd-menu-title">Visible to</p>

                                {[
                                    ['admin', 'Admins'],
                                    ['director', 'Directors'],
                                    ['staff', 'Staff'],
                                    ['teacher', 'Teachers'],
                                    ['parent', 'Parents'],
                                ].map(([value, label]) => (
                                    <label key={value} className="dnd-check-row">
                                        <input
                                            type="checkbox"
                                            checked={dndVisibleTo.includes(value)}
                                            onChange={() =>
                                                setDndVisibleTo((prev) =>
                                                    prev.includes(value)
                                                        ? prev.filter((item) => item !== value)
                                                        : [...prev, value]
                                                )
                                            }
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>

                            <div className="dnd-visible-section">
                                <p className="dnd-menu-title">Notify people</p>

                                <label className="dnd-check-row">
                                    <input
                                        type="checkbox"
                                        checked={sendInAppNotification}
                                        onChange={(e) => setSendInAppNotification(e.target.checked)}
                                    />
                                    In-app notification
                                </label>

                                <label className="dnd-check-row">
                                    <input
                                        type="checkbox"
                                        checked={sendEmailNotification}
                                        onChange={(e) => setSendEmailNotification(e.target.checked)}
                                    />
                                    Email notification
                                </label>

                                <label className="dnd-check-row">
                                    <input
                                        type="checkbox"
                                        checked={sendPushNotification}
                                        onChange={(e) => setSendPushNotification(e.target.checked)}
                                    />
                                    Push notification
                                </label>

                                {staffBlockType === 'dnd' && (
                                    <>
                                        <p className="dnd-section-title">
                                            Allow During Do Not Disturb
                                        </p>

                                        <label className="dnd-check-row">
                                            <input type="checkbox" defaultChecked />
                                            Emergency alerts
                                        </label>

                                        <label className="dnd-check-row">
                                            <input type="checkbox" defaultChecked />
                                            Pickup emergencies
                                        </label>

                                        <label className="dnd-check-row">
                                            <input type="checkbox" defaultChecked />
                                            Director/Admin override
                                        </label>

                                        <label className="dnd-check-row">
                                            <input type="checkbox" defaultChecked />
                                            Incident reports
                                        </label>
                                    </>
                                )}
                            </div>

                            <label>
                                Notes
                                <textarea
                                    value={dndNotes}
                                    onChange={(e) => setDndNotes(e.target.value)}
                                    placeholder="Optional notes..."
                                    rows={3}
                                />
                            </label>

                            {/* ----- Save / Cancel Actions ----- */}
                            <div className="status-modal-actions">
                                <button
                                    type="button"
                                    className="status-cancel-btn"
                                    onClick={() => {
                                        setShowCreateDndSchedule(false);
                                        setShowDndScheduleModal(false);
                                        setEditingDndScheduleId(null);

                                        setSelectedStaffId('');
                                        setStaffBlockType('dnd');

                                        setDndScheduleName('');
                                        setDndStartDate('');
                                        setDndEndDate('');
                                        setDndStartTime('09:00');
                                        setDndEndTime('10:00');
                                        setDndRepeat('none');
                                        setDndRepeatDays([]);
                                        setDndVisibleTo(['admin', 'director', 'staff']);
                                        setDndNotes('');

                                        setSendInAppNotification(true);
                                        setSendEmailNotification(false);
                                        setSendPushNotification(false);

                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="status-save-btn"
                                    onClick={async () => {
                                        if (!selectedStaffId) {
                                            toast.error('Select staff first');
                                            return;
                                        }

                                        const startTime = dndStartTime;
                                        const endTime = dndEndTime;

                                        const displayStartTime = formatScheduleDisplayTime(dndStartTime);
                                        const displayEndTime = formatScheduleDisplayTime(dndEndTime);

                                        if (!startTime || !endTime) {
                                            toast.error('Enter start and end time');
                                            return;
                                        }

                                        if (dndRepeat === 'none' && !dndStartDate) {
                                            toast.error('Select a start date for one-time focus blocks');
                                            return;
                                        }

                                        if (
                                            (dndRepeat === 'weekly' || dndRepeat === 'custom') &&
                                            dndRepeatDays.length === 0
                                        ) {
                                            toast.error('Select at least one day');
                                            return;
                                        }

                                        if (dndVisibleTo.length === 0) {
                                            toast.error('Select who can see this');
                                            return;
                                        }

                                        const buildFocusExpiresAt = (dateValue, timeValue) => {
                                            if (!dateValue || !timeValue) return null;

                                            let hour;
                                            let minute;

                                            if (/^\d{2}:\d{2}$/.test(timeValue)) {
                                                [hour, minute] = timeValue.split(':').map(Number);
                                            } else {
                                                const match = timeValue.match(/(\d+):(\d+)\s?(AM|PM)/i);
                                                if (!match) return null;

                                                hour = Number(match[1]);
                                                minute = Number(match[2]);

                                                const period = match[3].toUpperCase();
                                                if (period === 'PM' && hour !== 12) hour += 12;
                                                if (period === 'AM' && hour === 12) hour = 0;
                                            }

                                            const expires = new Date(`${dateValue}T00:00:00`);
                                            expires.setHours(hour, minute, 0, 0);

                                            return expires.toISOString();
                                        };

                                        const focusExpiresAt = buildFocusExpiresAt(dndStartDate, endTime);


                                        const newSchedule = {
                                            id: Date.now(),
                                            name: selectedBlockType?.label || 'Do Not Disturb',
                                            startTime,
                                            endTime,
                                            startDate: dndStartDate || null,
                                            endDate: dndEndDate || null,
                                            repeat: dndRepeat,
                                            expiresAt: focusExpiresAt,
                                            repeatDays: dndRepeatDays,
                                            visibleTo: dndVisibleTo,
                                            notes: dndNotes,
                                            sendInAppNotification,
                                            sendEmailNotification,
                                            useCenterTimezone,
                                            sendPushNotification,
                                            blockCalendar: true,
                                            allowAdminOverride: true,
                                            appliesToCalendar: true,
                                            appliesToMessages: true,
                                            timezone: useCenterTimezone
                                                ? 'America/Chicago'
                                                : Intl.DateTimeFormat().resolvedOptions().timeZone,
                                            enabled: true,
                                            bypassRules: dndBypassRules,
                                            blockType: staffBlockType,
                                            blockLabel: selectedBlockType?.label || 'Do Not Disturb',
                                            type: selectedBlockType?.type || 'dnd',
                                            icon: selectedBlockType?.icon || '⛔',
                                            monthlyRepeatMode,
                                            monthlyRepeatDates,
                                            yearlyRepeatMode,
                                            yearlyRepeatMonths,
                                            repeatDayOfMonth: dayOfMonth,
                                            weekNumber: weekNumber,
                                            repeatWeekday: weekdayName,
                                            repeatMonthName: monthName,
                                            assignedTo: selectedStaffId,
                                            assignedStaffName: selectedStaffName,
                                        };

                                        try {
                                            const isEditing = !!editingDndScheduleId;

                                            const endpoint = isEditing
                                                ? `/api/focus-schedules/${editingDndScheduleId}`
                                                : '/api/focus-schedules';

                                            const method = isEditing ? 'PATCH' : 'POST';

                                            const res = await fetch(endpoint, {
                                                method,
                                                headers: getAuthHeaders(),
                                                body: JSON.stringify(newSchedule),
                                            });

                                            if (!res.ok) {
                                                throw new Error('Failed to save focus schedule');
                                            }

                                            const savedSchedule = await res.json();

                                            const calendarRes = await fetch('/api/calendar', {
                                                method: 'POST',
                                                headers: getAuthHeaders(),
                                                body: JSON.stringify({
                                                    title: `${selectedBlockType?.icon || ''} ${selectedStaffName} - ${selectedBlockType?.label || 'Staff Block'}`,
                                                    description: dndNotes || selectedBlockType?.label || 'Staff Block',
                                                    eventDate: dndStartDate
                                                        ? new Date(`${dndStartDate}T${startTime}`).toISOString()
                                                        : new Date().toISOString(),
                                                    type: 'staff_block',
                                                    centerId: null,
                                                    assignedTo: selectedStaffId,
                                                    assignedStaffName: selectedStaffName,
                                                    linkedScheduleId: savedSchedule._id || savedSchedule.id,
                                                    createdBy: 'admin',
                                                    type: 'staff_block',
                                                }),
                                            });

                                            if (!calendarRes.ok) {
                                                const err = await calendarRes.text();
                                                console.error('Calendar save failed:', err);
                                            }
                                            window.dispatchEvent(new Event('kuddle:calendar-updated'));

                                            await fetch('/api/availability', {
                                                method: 'POST',
                                                headers: getAuthHeaders(),
                                                body: JSON.stringify({
                                                    type: selectedBlockType?.type || 'dnd',
                                                    text: selectedBlockType?.label || 'Do Not Disturb',
                                                    label: selectedBlockType?.label || 'Do Not Disturb',
                                                    blockLabel: selectedBlockType?.label || 'Do Not Disturb',
                                                    blockType: staffBlockType,
                                                    icon: selectedBlockType?.icon || '⛔',
                                                    source: 'custom_schedule',
                                                    startTime,
                                                    endTime,
                                                    startDate: dndStartDate || null,
                                                    endDate: dndEndDate || null,
                                                    repeat: dndRepeat,
                                                    repeatDays: dndRepeatDays,
                                                    visibleTo: dndVisibleTo,
                                                    notes: dndNotes,
                                                    expiresAt: focusExpiresAt,
                                                    enabled: true,
                                                    bypassRules: dndBypassRules,
                                                    useCenterTimezone,
                                                    monthlyRepeatMode,
                                                    monthlyRepeatDates,
                                                    yearlyRepeatMode,
                                                    yearlyRepeatMonths,
                                                    repeatDayOfMonth: dayOfMonth,
                                                    weekNumber: weekNumber,
                                                    repeatWeekday: weekdayName,
                                                    repeatMonthName: monthName,
                                                    assignedTo: selectedStaffId,
                                                    assignedStaffName: selectedStaffName,
                                                    userName: selectedStaffName,


                                                }),
                                            });

                                            if (isEditing) {
                                                setSavedDndSchedules((prev) =>
                                                    prev.map((item) =>
                                                        (item._id || item.id) === editingDndScheduleId
                                                            ? savedSchedule
                                                            : item
                                                    )
                                                );
                                            } else {
                                                setSavedDndSchedules((prev) => [savedSchedule, ...prev]);
                                            }

                                            setTimeout(() => {
                                                window.dispatchEvent(new Event('kuddle:availability-updated'));
                                                window.dispatchEvent(new Event('kuddle:focus-schedules-updated'));
                                            }, 150);

                                            setEditingDndScheduleId(null);

                                        } catch (err) {
                                            console.error(err);
                                            toast.error('Unable to save');
                                            return;
                                        }

                                        const scheduleDaysLabel =
                                            dndRepeat === 'weekly' || dndRepeat === 'custom'
                                                ? dndRepeatDays.join(', ')
                                                : dndRepeat === 'daily'
                                                    ? 'Daily'
                                                    : dndRepeat === 'monthly'
                                                        ? 'Monthly'
                                                        : 'One-time';

                                        const focusStatus = {
                                            type: selectedBlockType?.type || 'dnd',
                                            text: selectedBlockType?.label || 'Do Not Disturb',
                                            duration: selectedBlockType?.label || 'Scheduled',
                                            untilTime: `${displayStartTime} - ${displayEndTime}`,
                                            expiresAt: focusExpiresAt,
                                            bypassRules: dndBypassRules,
                                            blockType: staffBlockType,
                                            icon: selectedBlockType?.icon || '⛔',
                                        };

                                        setCurrentStatus(focusStatus);

                                        localStorage.setItem(
                                            'kuddlecore_active_status',
                                            JSON.stringify(focusStatus)
                                        );

                                        window.dispatchEvent(new Event('kuddle:availability-updated'));

                                        setDndStartTime('19:00');
                                        setDndEndTime('07:00');
                                        setDndStartDate('');
                                        setDndEndDate('');
                                        setDndRepeat('none');
                                        setDndRepeatDays([]);
                                        setDndVisibleTo(['admin', 'director', 'staff']);
                                        setDndNotes('');
                                        setSendInAppNotification(true);
                                        setSendEmailNotification(false);

                                        setShowCreateDndSchedule(false);
                                        setShowDndScheduleModal(false);
                                        setSelectedStaffId('');
                                        setStaffBlockType('dnd');

                                    }}
                                >
                                    Save Block
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div >
    );
};

export default DndScheduleModal;